import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./PsqiDermaDetails.css";

const getDifficultyLevel = (score) => {
  if (score === 0) return { text: "No sleep difficulty", color: "#888" };
  if (score <= 7) return { text: "Mild difficulty", color: "#4098e3" };
  if (score <= 14) return { text: "Moderate difficulty", color: "#f3d53a" };
  if (score <= 21) return { text: "Severe difficulty", color: "#e67d1b" };
  return { text: "", color: "" };
};

const getReason = (score) => {
  if (score === 0) return "Your sleep quality is unaffected by Eczema";
  if (score <= 7) return "You are experiencing mild difficulty in sleep because of Eczema";
  if (score <= 14) return "You are experiencing moderate difficulty in sleep because of Eczema";
  if (score <= 21) return "You are experiencing severe difficulty in sleep because of Eczema";
  return "";
};

const PsqiDermaDetails = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const type = urlParams.get("type") || "";

  const [score, setScore] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [logdate, setLogDate] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [nextDate, setNextDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchPsqiScore()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setScore(data.score);
          setReportUrl(data.reportUrl || "");
          setLogDate(data.logDate || "");
        }
      })
      .catch((err) => console.error("fetchPsqiScore:", err))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    if (logdate) {
      const next = new Date(Date.parse(logdate) + 30 * 24 * 60 * 60 * 1000);
      setNextDate(next);
      if (next > new Date()) setShowPopup(true);
    }
  }, [logdate]);

  const formatDate = (d) => {
    if (!d) return "";
    const obj = d instanceof Date ? d : new Date(d);
    return `${obj.getDate()}-${obj.toLocaleString("default", { month: "short" })}-${obj.getFullYear().toString().slice(-2)}`;
  };

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const difficulty = score != null ? getDifficultyLevel(score) : { text: "", color: "" };
  const reason = score != null ? getReason(score) : "";

  return (
    <>
      <section className="psqid-page">
        <div className="psqid-container">
          {loading ? (
            <div className="psqid-loading">{t("common.loading", "Loading...")}</div>
          ) : (
            <>
              <p className="psqid-description">
                {!type
                  ? t("psqiDetails.description", "PSQI (Pittsburgh Sleep Quality Index) is a questionnaire to evaluate your sleep difficulty in the past month.")
                  : reason}
              </p>

              {!type && (
                <div className="psqid-take-btn-wrap">
                  {showPopup ? (
                    <div className="psqid-completed-box">
                      {t("psqiDetails.completedOn", "You have completed a PSQI on")}{" "}
                      <strong>{formatDate(logdate)}</strong>.{" "}
                      {t("psqiDetails.nextOn", "The next one is on")} {formatDate(nextDate)}.
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="psqid-take-btn"
                      onClick={() => { window.location.href = `/psqi-calculator?${buildQs()}`; }}
                    >
                      {t("psqiDetails.takeBtn", "TAKE A PSQI")}
                    </button>
                  )}
                </div>
              )}

              {score != null && (
                <>
                  <label className="psqid-label">{t("psqiDetails.previousLabel", "Previous PSQI")}</label>
                  <div className="psqid-score-card">
                    <div style={{ flex: 1 }}>
                      <p className="psqid-difficulty-text" style={{ color: difficulty.color || "var(--text-primary)" }}>
                        {difficulty.text}
                      </p>
                      <div className="psqid-difficulty-bar" style={{ background: difficulty.color || "#ccc" }} />
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <p className="psqid-score-value">{score}</p>
                      <p className="psqid-score-max">(Out of 21)</p>
                    </div>
                    {reportUrl ? (
                      <a
                        href={`https://goqii.com/app?fsn=244&fssn=0&fai=${encodeURIComponent(JSON.stringify({ url: reportUrl, title: "PSQI-Report" }))}`}
                        className="psqid-report-link"
                      >
                        {t("psqiDetails.viewReport", "View Report")}
                      </a>
                    ) : null}
                  </div>

                  <div className="psqid-view-all-wrap">
                    <a
                      href={`https://goqii.com/app?fsn=242&fssn=3&fai=${encodeURIComponent(JSON.stringify({ url: `https://lifestyle1.goqii.com/self-stats?uuid=${uuid}`, title: "Flareup" }))}`}
                      className="psqid-view-all-link"
                    >
                      {t("psqiDetails.viewAll", "View All Previous PSQI")}
                    </a>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default PsqiDermaDetails;
